import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_LANGUAGE,
  toLanguageOptions,
} from '@/inpatient-patient-pwa/i18n/constants';
import {
  getAppSupportedLanguages,
  storeAppSupportedLanguages,
} from '@/inpatient-patient-pwa/services/storage';
import languageContext from './languageContext';

export interface Props {
  children: React.ReactNode;
}

/**
 * Holds the languages the hospital enabled and keeps the app inside that set.
 *
 * The tags arrive from `pwaLoginHospitalInfo` on the login screen (see
 * `setSupportedLanguages`) and are cached in local storage, so every later boot
 * knows the set before the query resolves.
 *
 * `null` means the config has not been read yet — kept apart from the empty
 * array (the hospital offers no choice at all) because only the former makes the
 * login screen wait, but both pin the app to pt-BR.
 */
const LanguageProvider: React.FunctionComponent<Props> = ({ children }) => {
  const { i18n } = useTranslation();
  const [supportedLanguages, setStoredLanguages] =
    React.useState<ReadonlyArray<string> | null>(getAppSupportedLanguages);

  const setSupportedLanguages = React.useCallback(
    (languages?: ReadonlyArray<string> | null) => {
      // An absent config is an answer, not a missing one: store it as the empty
      // set so this and every later boot pin the app to DEFAULT_LANGUAGE.
      const resolved = languages ?? [];
      storeAppSupportedLanguages(resolved);
      setStoredLanguages(resolved);
    },
    [],
  );

  const options = React.useMemo(
    () => (supportedLanguages ? toLanguageOptions(supportedLanguages) : []),
    [supportedLanguages],
  );

  /**
   * A language outside the hospital's set must never reach the screen — the
   * detector may have picked it from the browser, or persisted it before the
   * hospital narrowed its config. With no options to honour (config unknown, or
   * known to enable nothing) the app is pinned to pt-BR; otherwise the first
   * option wins, which is pt-BR whenever it is enabled since
   * `SUPPORTED_LANGUAGES` leads with it.
   *
   * This is what makes the device's own language irrelevant until the hospital's
   * config says otherwise: the detector's pick only survives if it is in the
   * set. Runs as a layout effect so the switch lands before the browser paints
   * the unavailable language.
   */
  React.useLayoutEffect(() => {
    if (options.some((option) => option.value === i18n.language)) return;

    i18n.changeLanguage(options[0]?.value ?? DEFAULT_LANGUAGE);
  }, [i18n, i18n.language, options]);

  const value = React.useMemo(
    () => ({
      options,
      isResolved: supportedLanguages !== null,
      setSupportedLanguages,
    }),
    [options, setSupportedLanguages, supportedLanguages],
  );

  return (
    <languageContext.Provider value={value}>
      {children}
    </languageContext.Provider>
  );
};

export default LanguageProvider;
